// This file defines the api that TeamPlan has towards the server.  It ties
// together the model objects as defined in `generatedApi.ts`, model objects
// from `model.ts`, `fetcher.ts`.
//

import {
  AcceptExchange,
  AssignCallInList,
  AssignShiftCandidate,
  CancelExchange,
  CancelOfferOnShiftExchange,
  EditShift,
  EndAbsence,
  GetAbsenceTypes,
  GetCallInList,
  GetDeviatingShiftInfo,
  GetEmployees,
  GetExchange,
  GetModifiedDutyLines,
  GetRemovedRegistrationInfo,
  GetSettings,
  GetShiftCandidateList,
  GetShiftDefs,
  GetTeamRoster,
  GetTeams,
  GetUserSettings,
  GetVersion,
  MarkAsHandled,
  OfferOnShiftExchange,
  RegisterAbsence,
  RemoveShift,
  SetFavoriteStatus,
  SetNotificationStatus,
  SystemInformation,
  TeamPlanSettings,
  TeamShift,
  TeamShiftDef,
  UserSettings,
} from '@pdcplan/shared/api/TeamPlan';
import { GetHolidays, HolidayResponse } from '@pdcplan/shared/api/Calendar';
import {
  Login_02,
  LoginOpenId_planner,
  Logout,
} from '@pdcplan/shared/api/Login';
import {
  GetMessages,
  GetUnreadCounts,
  MarkMessagesAsRead,
  PostMessage,
} from '@pdcplan/shared/api/Message';
import { GetGlobalSettings, GlobalSettings } from '@pdcplan/shared/api/Common';

import {
  ApplicationEnum,
  ExchangeCandidateFilterEnum,
  ExchangeTypeEnum,
  FavoriteStatusEnum,
  MsgVisibilityOldEnum,
  NotificationStatusEnum,
  PushProviderTypeEnum,
} from '@pdcplan/shared/enums';
import {
  CreatedShift,
  EditedShift,
  isCreatedShift,
} from '../ListView/CreateShift/EditedShift';
import {
  GetAllowNotifications,
  RegisterDevice,
  SetAllowNotifications,
} from '@pdcplan/shared/api/PushNotification';
import { getCallInData, getTeamRoster, toIdMap } from './model';
import { ReportTracking, TrackingObject } from '@pdcplan/shared/api/Tracking';

import { Period } from '../util/dates';
import { dateMath } from '@pdcfrontendui/utils';
import { hashPassword } from '../util/passwordHandling';
import { Api as BaseApi } from '@pdcplan/shared/api';

import { ApiError, HttpError, ValidationError } from '@pdcfrontendui/staffplan';
import {
  localStorageGet,
  localStorageRemove,
  localStorageSet,
} from '../util/localStorage';

import { LocalStorageKey } from '../util/LocalStorageKey';
import { isCordovaApp } from '@pdcshell/helpers/utils';
import { logout } from '../Login/LoginAction';
import { showErrorModal } from '../appActions';
import { store } from '../storeConfiguration';
import { getLangState } from '../hooks/useLang';
import { currentLanguage } from '../currentLanguage';

// Map endpoint keys to error titles dynamically
function getErrorTitles(): Record<string, string> {
  return {
    Login_02: currentLanguage.logonBiometricAuthErrorTitle,
    LoginOpenId_planner: currentLanguage.logonBiometricAuthErrorTitle,
    Logout: currentLanguage.Problem,
    GetVersion: currentLanguage.getVersionErrorTitle,
    GetGlobalSettings: currentLanguage.getSettingsErrorTitle,
    GetSettings: currentLanguage.getSettingsErrorTitle,
    GetUserSettings: currentLanguage.getSettingsErrorTitle,
    GetHolidays: currentLanguage.getHolidaysErrorTitle,
    GetTeams: currentLanguage.getTeamsErrorTitle,
    SetFavoriteStatus: currentLanguage.setFavoriteStatusErrorTitle,
    SetNotificationStatus: currentLanguage.setNotificationStatusErrorTitle,
    GetEmployees: currentLanguage.getEmployeesErrorTitle,
    GetTeamRoster: currentLanguage.getTeamRosterErrorTitle,
    OfferOnShiftExchange: currentLanguage.offerOnShiftExchangeErrorTitle,
    CancelOfferOnShiftExchange:
      currentLanguage.cancelOfferOnShiftExchangeErrorTitle,
    GetAbsenceTypes: currentLanguage.getAbsenceTypesErrorTitle,
    RegisterAbsence: currentLanguage.registerAbsenceErrorTitle,
    MarkAsHandled: currentLanguage.markAsHandledErrorTitle,
    GetCallInList: currentLanguage.GetCallInErrorTitle,
    AssignCallInList: currentLanguage.assignShiftErrorTitle,
    ReportTracking: currentLanguage.Error,
    GetAllowNotifications: currentLanguage.Problem,
    SetAllowNotifications: currentLanguage.Problem,
    RegisterDevice: currentLanguage.Error,
    GetMessages: currentLanguage.getMessagesErrorTitle,
    PostMessage: currentLanguage.postMessagesErrorTitle,
    MarkMessagesAsRead: currentLanguage.markMessagesAsReadErrorTitle,
    GetUnreadCounts: currentLanguage.getUnreadMessagesCountErrorTitle,
    GetExchange: currentLanguage.getExchangeErrorTitle,
    AcceptExchange: currentLanguage.exchangeAcceptErrorTitle,
    CancelExchange: currentLanguage.exchangeRejectErrorTitle,
    GetShiftDefs: currentLanguage.getShiftDefsErrorTitle,
    GetModifiedDutyLines: currentLanguage.getModifiedDutyLinesErrorTitle,
    GetShiftCandidateList: currentLanguage.getShiftCandidateListErrorTitle,
    AssignShiftCandidate: currentLanguage.assignShiftCandidateErrorTitle,
    RemoveShift: currentLanguage.removeShiftErrorTitle,
    EndAbsence: currentLanguage.endAbsenceErrorTitle,
    GetDeviatingShiftInfo: currentLanguage.Problem,
    GetRemovedRegistrationInfo:
      currentLanguage.getRemovedRegistrationInfoErrorTitle,
    EditShift: currentLanguage.editShiftErrorTitle,
  };
}

const onError = (
  error: ApiError | HttpError | ValidationError,
  method?: string
) => {
  if (!error.url?.match(/Log2/i)) {
    const httpCode =
      error instanceof ApiError
        ? error.status
        : error instanceof HttpError
          ? error.httpStatus
          : 0;
    if (httpCode === 401) {
      void store.dispatch(logout(false));
      return;
    } else if (
      httpCode === 500 ||
      (isCordovaApp() && error instanceof HttpError)
    ) {
      // The load balancer transforms all http error codes into 500, so this could
      // really be a 401 (unauthorized) because the jwt has expired.
      // If the jwt is older than 30 minutes (default jwt lifetime),
      // it's assumed that the jwt is the cause of the error, and the user is logged out.
      // RATJ: added instance of httpError when cordova, since CORS errors can occur in the app here, because the headers are gone when 500 from loadbalancer
      const tokenTimestamp = localStorageGet(LocalStorageKey.tokenTimestamp);
      if (tokenTimestamp) {
        const now = new Date().getTime();
        if (now - tokenTimestamp > 30 * 60 * 1000) {
          void store.dispatch(logout(false));
          return;
        }
      }
    }
    const errorTitles = getErrorTitles();
    const title = (method && errorTitles[method]) || currentLanguage.Error;
    store.dispatch(showErrorModal(error, title));
  }
};

export class Api extends BaseApi {
  public override baseUrl = document.baseURI
    .replace(/#.*/, '')
    .replace(/\/+$/, '')
    .toLowerCase();

  constructor() {
    super({
      version: import.meta.env.VITE_TEAMPLAN_VERSION,
      applicationId: ApplicationEnum.teamPlan,
      getToken: () => localStorageGet(LocalStorageKey.token) ?? '',
      setToken: (token) => {
        localStorageSet(LocalStorageKey.token, token);
        localStorageSet(LocalStorageKey.tokenTimestamp, new Date().getTime());
      },
      removeToken: () => {
        localStorageRemove(LocalStorageKey.token);
        localStorageRemove(LocalStorageKey.tokenTimestamp);
      },
      onAuthError: () => void store.dispatch(logout(false)),
      // Pass method name to error handler if available
      onApiError: (error: ApiError, method?: string) => {
        onError(error, method);
      },
      onValidationError: (validationError: ValidationError) => {
        onError(validationError);
      },
      onHttpError: onError,
      getLangCode: () => getLangState().currentLangCode,
    });
  }

  login(username: string, password: string) {
    const passwordHash = hashPassword(username, password);
    const obj = new Login_02(username, passwordHash, ApplicationEnum.teamPlan);
    return this.fetch(obj, { allowOnApiError: false });
  }

  loginOpenId(accessCode: string, redirectUri: string) {
    const obj = new LoginOpenId_planner(accessCode, redirectUri);
    return this.fetch(obj, { allowOnApiError: false });
  }

  logout() {
    const obj = new Logout();
    return this.fetch(obj, {
      removeTokenOnRequest: true,
    });
  }

  getVersion(): Promise<SystemInformation> {
    const obj = new GetVersion();
    return this.fetch(obj);
  }
  getGlobalSettings(): Promise<GlobalSettings> {
    const obj = new GetGlobalSettings();
    return this.fetch(obj);
  }

  getSettings(): Promise<TeamPlanSettings> {
    const obj = new GetSettings();
    return this.fetch(obj);
  }

  getUserSettings(): Promise<UserSettings> {
    const obj = new GetUserSettings();
    return this.fetch(obj);
  }

  getHolidays(): Promise<HolidayResponse> {
    const obj = new GetHolidays();
    return this.fetch(obj);
  }

  getTeams() {
    const obj = new GetTeams();
    return this.fetch(obj).then(toIdMap);
  }

  setFavoriteStatus(teamId: string, status: FavoriteStatusEnum) {
    const obj = new SetFavoriteStatus(teamId, status);
    return this.fetch(obj);
  }

  setNotificationStatus(teamId: string, status: NotificationStatusEnum) {
    const obj = new SetNotificationStatus(teamId, status);
    return this.fetch(obj);
  }

  getEmployees(personIds: number[], teamId: string) {
    const obj = new GetEmployees(personIds, teamId);
    return this.fetch(obj).then(toIdMap);
  }

  getTeamRoster(teamId: string, period: Period, isPoll: boolean) {
    const obj = new GetTeamRoster(teamId, period);
    return this.fetch(obj, { isPoll, allowErrors: !isPoll }).then((data) => {
      return getTeamRoster(data);
    });
  }

  offerOnShiftExchange(
    shiftId: string,
    teamIdList: string[],
    candidateFilter: ExchangeCandidateFilterEnum,
    remark: string
  ) {
    const obj = new OfferOnShiftExchange(
      shiftId,
      teamIdList,
      candidateFilter,
      remark
    );
    return this.fetch(obj);
  }

  cancelOfferOnShiftExchange(shiftId: string, teamId: string) {
    const obj = new CancelOfferOnShiftExchange(shiftId, teamId);
    return this.fetch(obj);
  }

  getAbsenceTypes(shiftId: string, teamId: string) {
    const obj = new GetAbsenceTypes(shiftId, teamId);
    return this.fetch(obj);
  }

  registerAbsence(
    shiftId: string,
    teamId: string,
    absenceTypeId: string,
    returnDate: Date | null
  ) {
    const obj = new RegisterAbsence(shiftId, teamId, absenceTypeId, returnDate);
    return this.fetch(obj);
  }

  markAsHandled(shiftId: string, teamId: string) {
    const obj = new MarkAsHandled(shiftId, teamId);
    return this.fetch(obj);
  }

  getCallIn(shiftId: string, teamId: string, updatedPeriod: Period | null) {
    const obj = new GetCallInList(shiftId, teamId, updatedPeriod);
    return this.fetch(obj).then((data) => {
      return getCallInData(data);
    });
  }

  assignShift(
    teamId: string,
    personId: number,
    shiftId: string,
    updatedPeriod: Period | null
  ) {
    const obj = new AssignCallInList(
      shiftId,
      teamId,
      [personId],
      updatedPeriod
    );
    return this.fetch(obj);
  }

  reportTracking(trackingObject: TrackingObject) {
    const obj = new ReportTracking(trackingObject);
    return this.fetch(obj);
  }

  getAllowNotifications() {
    const obj = new GetAllowNotifications();
    return this.fetch(obj);
  }

  setAllowNotifications(allowNotification: boolean) {
    const obj = new SetAllowNotifications(allowNotification);
    return this.fetch(obj);
  }

  registerDevice(deviceToken: string, serviceType: PushProviderTypeEnum) {
    const obj = new RegisterDevice(deviceToken, serviceType);
    return this.fetch(obj, { allowErrors: false, allowOnApiError: false });
  }

  async getMessages(
    personIds: number[],
    from: Date,
    to: Date,
    isPoll: boolean
  ) {
    const obj = new GetMessages(personIds, {
      from: dateMath.floorDay(from),
      to: dateMath.floorDay(to),
    });
    return await this.fetch(obj, {
      allowErrors: !isPoll,
      isPoll,
    });
  }

  async postMessage(
    day: Date,
    personId: number,
    messageText: string,
    isInternalMessage: boolean
  ) {
    const obj = new PostMessage(
      personId,
      day,
      messageText,
      isInternalMessage
        ? MsgVisibilityOldEnum.planner
        : MsgVisibilityOldEnum.employee
    );
    return await this.fetch(obj);
  }

  async markMessagesAsRead(messageIds: number[]) {
    const obj = new MarkMessagesAsRead(messageIds);
    // Only show error once pr. session as this is called on component mounts
    return await this.fetch(obj, { maxOnApiErrorCalls: 1 });
  }

  async getUnreadMessagesCount(
    teamIds: string[],
    from: Date,
    to: Date,
    isPoll: boolean
  ) {
    const obj = new GetUnreadCounts(teamIds, {
      from: dateMath.floorDay(from),
      to: dateMath.floorDay(to),
    });
    return await this.fetch(obj, {
      isPoll,
      allowErrors: !isPoll,
    });
  }

  getExchange(personId: number, shiftStart: Date) {
    const obj = new GetExchange(personId, shiftStart);
    return this.fetch(obj);
  }

  exchangeAccept(
    unitidId: string,
    requestExchangePeriod: Period,
    offerPersonId: number,
    offerExchangeId: number,
    requestPersonId: number | null
  ) {
    const obj = new AcceptExchange(
      unitidId,
      requestExchangePeriod,
      offerPersonId,
      offerExchangeId,
      requestPersonId
    );
    return this.fetch(obj);
  }

  exchangeReject(
    exchangeId: number,
    exchangePeriod: Period,
    exchangeUnitId: string,
    exchangeType: ExchangeTypeEnum,
    remark: string | null
  ) {
    const obj = new CancelExchange(
      exchangeId,
      exchangePeriod,
      exchangeUnitId,
      exchangeType,
      remark
    );
    return this.fetch(obj);
  }

  async getShiftDefs(teamId: string, period: Period) {
    const obj = new GetShiftDefs(teamId, period);
    return await this.fetch(obj, { maxOnApiErrorCalls: 1 });
  }

  async getModifiedDutyLines(
    shift: CreatedShift | (EditedShift & { def: TeamShiftDef })
  ) {
    const obj = new GetModifiedDutyLines(
      shift.def.unitid,
      shift.from,
      shift.def.id,
      { from: shift.from, to: shift.to }
    );
    return await this.fetch(obj);
  }

  async getShiftCandidateList(teamId: string, shift: CreatedShift) {
    const obj = new GetShiftCandidateList(teamId, shift.from, shift.def.id, {
      from: shift.from,
      to: shift.to,
    });
    return await this.fetch(obj);
  }

  async assignShiftCandidate(
    teamId: string,
    personId: number,
    shift: CreatedShift | EditedShift
  ) {
    const obj = new AssignShiftCandidate(
      teamId,
      shift.from,
      personId,
      isCreatedShift(shift)
        ? shift.def.id
        : shift.def
          ? shift.def.id
          : shift.originalShift.shiftDefId,
      { from: shift.from, to: shift.to }
    );
    return await this.fetch(obj);
  }

  removeShift(shiftId: string, teamId: string) {
    const obj = new RemoveShift(shiftId, teamId);
    return this.fetch(obj);
  }

  endAbsence(shiftId: string, teamId: string) {
    const obj = new EndAbsence(shiftId, teamId);
    return this.fetch(obj);
  }

  getDeviatingShiftInfo(teamId: string, shiftId: string) {
    const obj = new GetDeviatingShiftInfo(teamId, shiftId);
    return this.fetch(obj);
  }

  getRemovedRegistrationInfo(
    teamId: string,
    shiftId: string,
    isDelete: boolean,
    updatedPeriod: Period | null
  ) {
    const obj = new GetRemovedRegistrationInfo(
      teamId,
      shiftId,
      isDelete,
      updatedPeriod
    );
    // We do error handling locally
    return this.fetch(obj, { allowOnApiError: false });
  }

  editShift(
    teamId: string,
    originalShift: TeamShift,
    editedShift: EditedShift
  ) {
    const obj = new EditShift(
      teamId,
      originalShift.id,
      editedShift.def?.id ?? originalShift.shiftDefId,
      { from: editedShift.from, to: editedShift.to }
    );
    return this.fetch(obj);
  }
}
